import React, { useState, useEffect } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from 'axios';
import { User, Mail, Phone, Camera, Edit3, Save, X } from 'lucide-react';
import { Tabs, Tab, Box, Typography } from "@mui/material";
import Rest from './Rest'
import "./Profile.css";

const API_BASE_URL = 'https://api.waraq.co/waraq/api/v1';
const AUTO_HIDE_MESSAGE_DELAY = 3000;

const validators = {
  required: (value) => !value?.trim() ? 'This field is required' : '',
  email: (value) => {
    if (!value?.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
    return '';
  },
  phoneNumber: (value) => {
    if (!value?.trim()) return 'Phone number is required';
    const cleaned = value.replace(/\s|\(|\)|\-/g, '');
    if (!/^[\+]?[1-9][\d]{0,15}$/.test(cleaned)) return 'Please enter a valid phone number';
    return '';
  }
};

const TabPanel = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
};

const ProfileAvatar = ({ imageUrl, isEditing, onImageUpload, isUploading }) => {
  return (
    <div className="avatar-container">
      {imageUrl ? (
        <img
          style={{
            width: '100px',
            height: '100px',
            objectFit: 'cover',
            borderRadius: '50%'
          }}
          src={imageUrl}
          alt="Profile"
        />
      ) : (
        <div className="avatar-circle">
          <User className="avatar-icon" />
        </div>
      )}
      {isEditing && (
        <label className="camera-button" style={{ cursor: isUploading ? 'not-allowed' : 'pointer', opacity: isUploading ? 0.6 : 1 }}>
          <Camera className="camera-icon" />
          <input
            type="file"
            accept="image/*"
            onChange={onImageUpload}
            className="hidden-input"
            disabled={isUploading}
          />
        </label>
      )}
      {isUploading && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '12px', color: '#666' }}>
          Uploading...
        </div>
      )}
    </div>
  );
};

const FormField = ({
  label,
  value,
  isEditing,
  error,
  icon: Icon,
  type = "text",
  placeholder,
  onChange
}) => {
  return (
    <div className="field-group">
      <label className="field-label">{label}</label>
      {isEditing ? (
        <div>
          <input
            type={type}
            value={value}
            onChange={onChange}
            className={`field-input ${error ? 'error' : ''}`}
            placeholder={placeholder}
          />
          {error && <p className="error-message">{error}</p>}
        </div>
      ) : (
        <div className="field-display">
          <Icon className="field-icon" />
          <span className="field-value">{value || 'Not provided'}</span>
        </div>
      )}
    </div>
  );
};

const PasswordInput = ({ value, onChange, placeholder, showPassword, onToggle, autoComplete }) => {
  return (
    <div style={{ marginBottom: '15px', position: 'relative' }}>
      <input
        type={showPassword ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        style={{
          width: '100%',
          padding: '10px',
          paddingRight: '40px',
          border: '1px solid #DDD',
          borderRadius: '4px',
          fontSize: '14px',
          boxSizing: 'border-box',
        }}
      />
      <span
        style={{
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          cursor: 'pointer',
          color: '#666',
        }}
        onClick={onToggle}
      >
        {showPassword ? <FaEyeSlash /> : <FaEye />}
      </span>
    </div>
  );
};

const SuccessMessage = ({ message }) => {
  if (!message) return null;
  return <div className="popup-message">{message}</div>;
};

const Profile = () => {
  const getAuthHeaders = () => {
    const token = localStorage.getItem("jwtToken");
    return { Authorization: `${token}` };
  };
  const loginType = localStorage.getItem("role");

  const [activeTab, setActiveTab] = useState(0);

  const [userData, setUserData] = useState(null);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    imageId: '',
    imageUrl: '',
  });
  const [formData, setFormData] = useState({ ...profileData });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  // const [profileError , setProfileError] = useState('')

  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    showOld: false,
    showNew: false,
    showConfirm: false,
  });
  const [successMessage, setSuccessMessage] = useState('');

  const getUserInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user/profile`, {
        headers: getAuthHeaders()
      });
      console.log('response.data.data', response.data.data);
      setUserData(response.data.data);

    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  const validateForm = () => {
    const newErrors = {
      firstName: validators.required(formData.firstName) || '',
      lastName: validators.required(formData.lastName) || '',
      email: validators.email(formData.email) || '',
    };

    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, value]) => value !== '')
    );

    setErrors(filteredErrors);
    return Object.keys(filteredErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setErrors({});
    setError('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({ ...profileData });
    setErrors({});
    setError('');
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/user/profile/update`,
        formData,
        { headers: getAuthHeaders() }
      );

      if (response.status === 200 || response.status === 201) {
        setProfileData({ ...formData });
        setIsEditing(false);
        await getUserInfo();
        setSuccessMessage('Profile updated successfully!');
        setTimeout(() => setSuccessMessage(''), AUTO_HIDE_MESSAGE_DELAY);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.data || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };



  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    console.log('file', file);

    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPEG, PNG, or GIF)');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Image size should be less than 5MB');
      return;
    }

    setIsUploadingImage(true);
    setError('');

    try {
      const formDataImage = new FormData();
      formDataImage.append("file", file);
      const upload = await axios.post(`${API_BASE_URL}/media/upload`, formDataImage, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      const DTO = {
        imageId: upload?.data?.data?.id,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phoneNumber: userData.phoneNumber || '',
      }
      const response = await axios.put(
        `${API_BASE_URL}/user/profile/update`,
        DTO,
        { headers: getAuthHeaders() }

      );


      if (response.status === 200 || response.status === 201) {
        const newImageData = response.data.data;

        setFormData(prev => ({
          ...prev,
          imageId: newImageData.imageId || newImageData.id,
          imageUrl: newImageData.imageUrl || newImageData.url
        }));

        setProfileData(prev => ({
          ...prev,
          imageId: newImageData.imageId || newImageData.id,
          imageUrl: newImageData.imageUrl || newImageData.url
        }));

        await getUserInfo();

        setSuccessMessage('Profile image updated successfully!');
        setTimeout(() => setSuccessMessage(''), AUTO_HIDE_MESSAGE_DELAY);
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.response?.data?.message || err.response?.data?.data || 'Failed to upload image');
    } finally {
      setIsUploadingImage(false);
      event.target.value = '';
    }
  };


  const handlePasswordChange = (field, value) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleUpdatePassword = async () => {
    setError('');

    // Validate passwords
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setError('All password fields are required');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    try {
      const res = await axios.patch(
        `${API_BASE_URL}/user/profile/password`,
        passwordForm,
        { headers: getAuthHeaders() }
      );
      setSuccessMessage('Password updated successfully!');
      setPasswordForm({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      setTimeout(() => setSuccessMessage(''), AUTO_HIDE_MESSAGE_DELAY);
      setTimeout(() => {
        window.location.reload();
      }, 3000);
      // window.location.reload();
    } catch (err) {
      console.error('Error updating password:', err);
      setError(err.response?.data?.data || err.response?.data?.message || 'Failed to update password');
    }
  };

  useEffect(() => {
    if (userData) {
      const newProfileData = {
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phoneNumber: userData.phoneNumber || '',
        imageId: userData.imageId || '',
        imageUrl: userData.imageUrl || '',
      };
      setProfileData(newProfileData);
      setFormData(newProfileData);
    }
  }, [userData]);

  useEffect(() => {
    getUserInfo();
  }, []);

  if (!userData) {
    return (
      <div className="profile-container">
        <div className="profile-wrapper">
          <div className="profile-card">
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              Loading profile...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Box sx={{ width: "100%", padding: '20px 100px' }}>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{
          '& .MuiTabs-indicator': {
            backgroundColor: '#3F3030',
            height: '2px',
          },
        }}
      >
        <Tab
          style={{
            color: "#3F3030",
          }}
          label="Profile Management"
        />
        <Tab
          style={{
            color: "#3F3030",
          }}
          label="Update Password"
        />

        {loginType === "ADMIN" && <Tab
          style={{
            color: "#3F3030",
          }}
          label="Rest Password"
        />}

      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <div className="profile-container">
          <div className="profile-wrapper">
            <div className="profile-card">
              <div className="profile-header">
                <div className="profile-header-center">
                  <ProfileAvatar
                    imageUrl={formData.imageUrl}
                    isEditing={isEditing}
                    onImageUpload={handleImageUpload}
                    isUploading={isUploadingImage}
                  />
                  <h1 className="profile-name">
                    {profileData.firstName} {profileData.lastName}
                  </h1>
                  <p className="profile-email">{profileData.email}</p>
                </div>
              </div>

              <div className="form-content">
                <div className="form-header">
                  <h2 className="form-title">Profile Information</h2>
                  {!isEditing ? (
                    <button onClick={handleEdit} className="edit-button">
                      <Edit3 className="button-icon" />
                      Edit Profile
                    </button>
                  ) : (
                    <div className="button-actions">
                      <button onClick={handleCancel} className="cancel-button">
                        <X className="button-icon" />
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdate}
                        disabled={isLoading}
                        className="update-button"
                      >
                        <Save className="button-icon" />
                        {isLoading ? 'Updating...' : 'Update Profile'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="form-fields">
                  <FormField
                    label="First Name"
                    value={formData.firstName}
                    isEditing={isEditing}
                    error={errors.firstName}
                    icon={User}
                    placeholder="Enter your first name"
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                  />

                  <FormField
                    label="Last Name"
                    value={formData.lastName}
                    isEditing={isEditing}
                    error={errors.lastName}
                    icon={User}
                    placeholder="Enter your last name"
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                  />

                  <FormField
                    label="Email Address"
                    value={formData.email}
                    isEditing={isEditing}
                    error={errors.email}
                    icon={Mail}
                    type="email"
                    placeholder="Enter your email address"
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />

                  <FormField
                    label="Phone Number"
                    value={formData.phoneNumber}
                    isEditing={isEditing}
                    error={errors.phoneNumber}
                    icon={Phone}
                    type="tel"
                    placeholder="Enter your phone number"
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  />
                </div>
              </div>

              {error && (
                <p style={{ marginBottom: '15px', color: "red", padding: '0 25px' }}>
                  {error}
                </p>
              )}
            </div>
          </div>
        </div>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '75vh',
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              width: '400px',
              boxShadow: '0 0 25px 8px rgba(0, 0, 0, 0.15)'
            }}
          >
            <h2 style={{ marginBottom: '20px' }}>Update Password</h2>

            <PasswordInput
              value={passwordForm.oldPassword}
              onChange={(e) => handlePasswordChange('oldPassword', e.target.value)}
              placeholder="Old Password"
              showPassword={passwordVisibility.showOld}
              onToggle={() => togglePasswordVisibility('showOld')}
              autoComplete="current-password"
            />

            <PasswordInput
              value={passwordForm.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
              placeholder="New Password"
              showPassword={passwordVisibility.showNew}
              onToggle={() => togglePasswordVisibility('showNew')}
              autoComplete="new-password"
            />

            <PasswordInput
              value={passwordForm.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
              placeholder="Confirm Password"
              showPassword={passwordVisibility.showConfirm}
              onToggle={() => togglePasswordVisibility('showConfirm')}
              autoComplete="new-password"
            />

            {error && <p style={{ color: 'red', marginBottom: '10px' }}>{error}</p>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={handleUpdatePassword}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  background: '#3F302F',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                Update Password
              </button>
            </div>
          </div>
        </div>

        <SuccessMessage message={successMessage} />
      </TabPanel>


      {loginType === "ADMIN" &&
        <TabPanel value={activeTab} index={2}>

          <Rest />

          <SuccessMessage message={successMessage} />
        </TabPanel>
      }


      <SuccessMessage message={successMessage} />
    </Box>
  );
};

export default Profile;
